import { useState } from "react"

import { useTranslation } from "react-i18next"

import { type ServerLogs } from "@/api/feedback-api"
import { type Lane } from "@/components/feedback-dialog"
import {
  Check,
  Copy,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { AttachmentPreview } from "./feedback-attachment-preview"
import { FieldsTable } from "./feedback-detail-fields"
import { ServerLogsTitleIcon, ServerLogsView } from "./feedback-server-logs-view"
import { TranscriptView } from "./feedback-transcript-view"
import {
  useAdminFeedbackDetailQuery,
  useHydrateAdminFeedbackMutation,
} from "../../hooks/admin-feedback-queries"
import { useAdminFeedbackAttachment } from "../../hooks/use-admin-feedback-attachment"

/**
 * Detail view for a single feedback record.
 *
 * Single-column scrollable layout. Surfaces the important structured IDs
 * (route, session, message, error, user, submitted) at the top so admins
 * don't have to spelunk through the captured-context JSON, then renders
 * the user-entered fields, attachments and raw JSON below with copy
 * helpers and a fullscreen screenshot view.
 */
export function FeedbackDetailDialog({
  id,
  open,
  onOpenChange,
}: {
  id: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const detail = useAdminFeedbackDetailQuery(id)
  const hydrate = useHydrateAdminFeedbackMutation()

  const [hydratedAt, setHydratedAt] = useState<number | null>(null)
  const [hydrated, setHydrated] = useState<unknown>(null)
  const [hydrateErr, setHydrateErr] = useState<string | null>(null)
  const [fullscreenSrc, setFullscreenSrc] = useState<string | null>(null)

  const item = detail.data?.item
  const hasEvidence = detail.data?.has_evidence ?? false
  const hasScreenshot = detail.data?.has_screenshot ?? false
  const evidence = useAdminFeedbackAttachment(id, "evidence", open && hasEvidence)
  const screenshot = useAdminFeedbackAttachment(id, "screenshot", open && hasScreenshot)
  const content = (item?.content ?? null) as Record<string, unknown> | null
  const attachmentErrorText = t(
    "adminDashboard.feedback.detail.attachmentFailed",
    "Could not load attachment."
  )
  const screenshotAlt = t("adminDashboard.feedback.detail.screenshot", "Screenshot at submit")
  const activeFullscreenSrc =
    fullscreenSrc && (fullscreenSrc === evidence.url || fullscreenSrc === screenshot.url)
      ? fullscreenSrc
      : null

  const context = (content?.context ?? {}) as Record<string, unknown>
  const client = (context.client ?? {}) as Record<string, unknown>
  const session = (context.session ?? {}) as Record<string, unknown>
  const response = (context.response ?? {}) as Record<string, unknown>
  const error = (context.error ?? {}) as Record<string, unknown>
  const user = (content?.user ?? {}) as Record<string, unknown>
  const sessionId = typeof session.session_id === "string" ? session.session_id : null
  // Optional for back-compat: records submitted before server-log capture
  // shipped lack `server_logs` entirely. `undefined` -> "Not captured" notice;
  // a present object with empty `entries` -> its `unavailable_reason`.
  const serverLogs =
    context.server_logs && typeof context.server_logs === "object"
      ? (context.server_logs as ServerLogs)
      : undefined

  const handleHydrate = async () => {
    if (!id) return
    setHydrateErr(null)
    try {
      const result = await hydrate.mutateAsync(id)
      setHydrated(result.session_context)
      setHydratedAt(Date.now())
    } catch (err: unknown) {
      setHydrateErr(
        errorMessage(err) ||
          t("adminDashboard.feedback.detail.hydrateFailed", "Could not load transcript.")
      )
      setHydratedAt(Date.now())
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100%-2rem)] flex-col sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t("adminDashboard.feedback.detail.title", "Feedback record")}
            </DialogTitle>
            {item && (
              <DialogDescription>
                {t(
                  `adminDashboard.feedback.lane.${item.type ?? "unknown"}`,
                  laneLabel(item.type as Lane | undefined)
                )}
                {" · "}
                {formatTimestamp(content?.submitted_at as string | null)}
              </DialogDescription>
            )}
          </DialogHeader>

          <DialogBody>
            {detail.isLoading || !content ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="text-muted-foreground size-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-6 text-sm">
                <AtAGlance
                  clientInfo={client}
                  errorId={typeof error.error_id === "string" ? error.error_id : null}
                  messageId={typeof response.message_id === "string" ? response.message_id : null}
                  sessionId={sessionId}
                  user={user}
                />

                <Section title={t("adminDashboard.feedback.detail.fields", "User-entered fields")}>
                  <FieldsTable fields={(content.fields ?? {}) as Record<string, unknown>} />
                </Section>

                {(hasEvidence || hasScreenshot) && (
                  <div className="grid gap-4 md:grid-cols-2">
                    {hasEvidence && (
                      <Section
                        title={
                          <TitleWithIcon>
                            <ImageIcon className="size-4" />
                            {t("adminDashboard.feedback.detail.evidence", "Evidence")}
                          </TitleWithIcon>
                        }
                      >
                        <AttachmentPreview
                          alt={t("adminDashboard.feedback.detail.evidence", "Evidence")}
                          errorText={attachmentErrorText}
                          hasError={evidence.hasError}
                          isLoading={evidence.isLoading}
                          src={evidence.url}
                          onFullscreen={() => evidence.url && setFullscreenSrc(evidence.url)}
                        />
                      </Section>
                    )}
                    {hasScreenshot && (
                      <Section
                        title={
                          <TitleWithIcon>
                            <ImageIcon className="size-4" />
                            {t("adminDashboard.feedback.detail.screenshot", "Screenshot at submit")}
                          </TitleWithIcon>
                        }
                      >
                        <AttachmentPreview
                          alt={screenshotAlt}
                          errorText={attachmentErrorText}
                          hasError={screenshot.hasError}
                          isLoading={screenshot.isLoading}
                          src={screenshot.url}
                          onFullscreen={() => screenshot.url && setFullscreenSrc(screenshot.url)}
                        />
                      </Section>
                    )}
                  </div>
                )}

                <Section
                  title={
                    <TitleWithIcon>
                      <ServerLogsTitleIcon />
                      {t("adminDashboard.feedback.detail.serverLogs", "Server logs")}
                    </TitleWithIcon>
                  }
                >
                  <ServerLogsView value={serverLogs} />
                </Section>

                <Section
                  title={t(
                    "adminDashboard.feedback.detail.context",
                    "Captured context (at submit)"
                  )}
                >
                  <JsonBlock value={context} />
                </Section>

                <Section
                  title={t("adminDashboard.feedback.detail.hydrate", "Live chat transcript")}
                >
                  <p className="text-muted-foreground text-xs">
                    {t(
                      "adminDashboard.feedback.detail.hydrateDescription",
                      "Re-fetch the session messages from the live store. Useful when you need more context than what was captured at submit time."
                    )}
                  </p>
                  {sessionId ? (
                    <Button
                      disabled={hydrate.isPending}
                      size="sm"
                      variant="outline"
                      onClick={handleHydrate}
                    >
                      {hydrate.isPending ? (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 size-4" />
                      )}
                      {t("adminDashboard.feedback.detail.loadTranscript", "Load full chat context")}
                    </Button>
                  ) : (
                    <p className="text-muted-foreground text-xs italic">
                      {t(
                        "adminDashboard.feedback.detail.hydrateNoSession",
                        "No session reference on this record — nothing to hydrate."
                      )}
                    </p>
                  )}
                  {hydrateErr && <p className="text-destructive text-xs">{hydrateErr}</p>}
                  {hydratedAt !== null && !hydrateErr && hydrated === null && sessionId && (
                    <p className="text-muted-foreground text-xs italic">
                      {t(
                        "adminDashboard.feedback.detail.hydrateEmpty",
                        "No transcript available — the session may have no retrievable messages."
                      )}
                    </p>
                  )}
                  {hydrated !== null && <TranscriptView value={hydrated} />}
                </Section>
              </div>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      {activeFullscreenSrc && (
        <ImageLightbox src={activeFullscreenSrc} onClose={() => setFullscreenSrc(null)} />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Structured-data header
// ---------------------------------------------------------------------------

function AtAGlance({
  user,
  sessionId,
  messageId,
  errorId,
  clientInfo,
}: {
  user: Record<string, unknown>
  sessionId: string | null
  messageId: string | null
  errorId: string | null
  clientInfo: Record<string, unknown>
}) {
  const { t } = useTranslation()
  const userName = typeof user.name === "string" ? user.name : null
  const userId = typeof user.id === "string" ? user.id : null
  const route = typeof clientInfo.route === "string" ? clientInfo.route : null
  const url = typeof clientInfo.url === "string" ? clientInfo.url : null
  const userAgent = typeof clientInfo.user_agent === "string" ? clientInfo.user_agent : null

  return (
    <div className="bg-muted/30 border-border grid gap-x-4 gap-y-2 rounded-md border p-3 text-xs sm:grid-cols-2">
      <Row label={t("adminDashboard.feedback.detail.reportedBy", "Reported by")}>
        {userName ?? "—"}
        {userId ? <CopyableMono text={userId} /> : null}
      </Row>
      <Row label={t("adminDashboard.feedback.detail.route", "Route")}>
        {route ? <CopyableMono text={route} /> : <em className="text-muted-foreground">—</em>}
      </Row>
      <Row label={t("adminDashboard.feedback.detail.sessionId", "Session ID")}>
        {sessionId ? (
          <CopyableMono text={sessionId} />
        ) : (
          <em className="text-muted-foreground">—</em>
        )}
      </Row>
      <Row label={t("adminDashboard.feedback.detail.messageId", "Message ID")}>
        {messageId ? (
          <CopyableMono text={messageId} />
        ) : (
          <em className="text-muted-foreground">—</em>
        )}
      </Row>
      <Row label={t("adminDashboard.feedback.detail.errorId", "Error ID")}>
        {errorId ? <CopyableMono text={errorId} /> : <em className="text-muted-foreground">—</em>}
      </Row>
      <Row label={t("adminDashboard.feedback.detail.url", "Page URL")}>
        {url ? (
          <a
            className="text-primary inline-flex max-w-full items-center gap-1 truncate hover:underline"
            href={url}
            rel="noopener noreferrer"
            target="_blank"
          >
            <span className="truncate">{url}</span>
            <ExternalLink className="size-3 shrink-0" />
          </a>
        ) : (
          <em className="text-muted-foreground">—</em>
        )}
      </Row>
      {userAgent ? (
        <Row
          className="sm:col-span-2"
          label={t("adminDashboard.feedback.detail.userAgent", "Browser")}
        >
          <span className="text-muted-foreground truncate">{userAgent}</span>
        </Row>
      ) : null}
    </div>
  )
}

function Row({
  label,
  children,
  className,
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("flex min-w-0 items-start gap-2", className)}>
      <span className="text-muted-foreground w-24 shrink-0 font-medium">{label}</span>
      <span className="flex min-w-0 flex-1 items-center gap-1.5">{children}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// JSON block with copy button
// ---------------------------------------------------------------------------

function JsonBlock({ value }: { value: unknown }) {
  const json = JSON.stringify(value, null, 2)
  return (
    <div className="relative">
      <pre className="bg-muted/40 max-h-80 overflow-auto rounded-md border p-3 pr-10 text-xs">
        {json}
      </pre>
      <div className="absolute top-2 right-2">
        <CopyButton text={json} />
      </div>
    </div>
  )
}

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          aria-label={t("common.copy", "Copy")}
          size="icon"
          variant="ghost"
          onClick={handleCopy}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t("common.copy", "Copy")}</TooltipContent>
    </Tooltip>
  )
}

function CopyableMono({ text }: { text: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className="hover:bg-accent/40 bg-background focus-visible:ring-ring inline-flex max-w-full min-w-0 items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[11px] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          type="button"
          onClick={handleCopy}
        >
          <span className="truncate">{text}</span>
          {copied ? (
            <Check className="text-success size-3 shrink-0" />
          ) : (
            <Copy className="text-muted-foreground size-3 shrink-0" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>{t("common.copy", "Copy")}</TooltipContent>
    </Tooltip>
  )
}

function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const { t } = useTranslation()
  // The Dialog shell supplies the focus trap, Escape handling, aria-modal
  // wiring, backdrop click-to-dismiss and the themed 40px close button —
  // dismissal is driven solely by onOpenChange, so it isn't duplicated by an
  // onClick on DialogContent (that would double-fire on the built-in close
  // button's click, since it bubbles up from a Content descendant).
  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-5xl items-center justify-center border-none bg-transparent p-0 shadow-none [&>button]:text-white">
        <DialogTitle className="sr-only">
          {t("adminDashboard.feedback.screenshotFullscreen", "Screenshot")}
        </DialogTitle>
        {/* Clicking the screenshot closes the lightbox (single dismiss path
            via Radix Close — no duplicated onClose on Content). */}
        <DialogClose
          aria-label={t("common.close", { defaultValue: "Close" })}
          className="focus-visible:ring-ring cursor-zoom-out rounded-md focus-visible:ring-2 focus-visible:outline-none"
        >
          <img
            alt=""
            className="max-h-[calc(100dvh-4rem)] max-w-full rounded-md object-contain shadow-2xl"
            src={src}
          />
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
        {title}
      </p>
      {children}
    </div>
  )
}

function TitleWithIcon({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1.5">{children}</span>
}

function laneLabel(lane: Lane | string | null | undefined): string {
  switch (lane) {
    case "response_feedback":
      return "Answer feedback"
    case "bug":
      return "Bug"
    case "incident":
      return "Incident"
    default:
      return "Unknown"
  }
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "—"
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

/**
 * Unwrap the most informative error string we can get out of whatever the
 * HTTP / query layer threw. Axios hides the server-provided `{error: "..."}`
 * body inside `response.data`; without this the UI only ever sees
 * "Request failed with status code 400", which is useless for debugging.
 */
function errorMessage(err: unknown): string {
  if (err && typeof err === "object") {
    const e = err as {
      response?: { data?: { error?: unknown; message?: unknown } | string }
      message?: unknown
    }
    const data = e.response?.data
    if (data && typeof data === "object") {
      if (typeof data.error === "string") return data.error
      if (typeof data.message === "string") return data.message
    }
    if (typeof data === "string") return data
    if (typeof e.message === "string") return e.message
  }
  if (err instanceof Error) return err.message
  return ""
}
